import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, X, Building2, Mail, Phone, MapPin as MapPinIcon, FileText } from 'lucide-react';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const empty = {
  name: '', region_id: '', contact_name: '', contact_email: '', contact_phone: '',
  billing_address: '', tax_id: '', country: '', state: '', notes: '', active: true,
};

const CustomersPage = () => {
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regionFilter, setRegionFilter] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | customer
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get('/customers', { params: regionFilter ? { region_id: regionFilter } : {} }),
        api.get('/regions'),
      ]);
      setCustomers(c.data);
      setRegions(r.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [regionFilter]);

  const startNew = () => { setForm(empty); setEditing('new'); setErr(''); };
  const startEdit = (c) => { setForm({ ...c }); setEditing(c); setErr(''); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      if (editing === 'new') {
        await api.post('/customers', form);
      } else {
        const { id, created_at, ...rest } = form;
        await api.patch(`/customers/${editing.id}`, rest);
      }
      setEditing(null);
      await load();
    } catch (ex) {
      setErr(errorMessage(ex, 'Failed to save customer'));
    }
    setSaving(false);
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete customer ${c.name}?`)) return;
    try { await api.delete(`/customers/${c.id}`); load(); } catch (e) { alert(errorMessage(e, 'Failed')); }
  };

  const getRegion = (id) => regions.find((r) => r.id === id);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] font-semibold uppercase text-emerald-300">
            <Building2 className="w-3.5 h-3.5" /> Customers
          </div>
          <h1 className="mt-2 text-3xl font-semibold text-white tracking-tight">Customers</h1>
          <p className="mt-1 text-white/55">Assign customers to a region. Customer-scoped price lists override region-scoped lists in the estimator.</p>
        </div>
        <button onClick={startNew} className="h-10 px-4 rounded-lg bg-emerald-400 hover:bg-emerald-300 text-black font-semibold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add customer
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/50">Filter by region</span>
        <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}
          className="h-9 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[13px] focus:outline-none focus:border-cyan-400/50">
          <option value="">All regions</option>
          {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-white/10 text-left text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50">
              <th className="px-6 py-4">Customer</th>
              <th className="px-4 py-4">Region</th>
              <th className="px-4 py-4">Contact</th>
              <th className="px-4 py-4">Tax ID</th>
              <th className="px-4 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-white/40">Loading…</td></tr>
            ) : customers.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-white/40">No customers yet</td></tr>
            ) : customers.map((c) => {
              const region = getRegion(c.region_id);
              return (
                <tr key={c.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.015]">
                  <td className="px-6 py-4">
                    <div className="font-medium text-white">{c.name}</div>
                    {c.country && <div className="text-[12px] text-white/50">{[c.state, c.country].filter(Boolean).join(', ')}</div>}
                  </td>
                  <td className="px-4 py-4">
                    {region ? (
                      <span className="inline-flex items-center gap-1.5 text-[12.5px] text-white/85">
                        <MapPinIcon className="w-3 h-3 text-cyan-300" /> {region.name}
                      </span>
                    ) : <span className="text-white/40">—</span>}
                  </td>
                  <td className="px-4 py-4 text-[12.5px]">
                    {c.contact_name && <div className="text-white/80">{c.contact_name}</div>}
                    {c.contact_email && <div className="text-white/50">{c.contact_email}</div>}
                  </td>
                  <td className="px-4 py-4 text-white/70 font-mono text-[12px]">{c.tax_id || '—'}</td>
                  <td className="px-4 py-4 text-right space-x-2">
                    <button onClick={() => startEdit(c)} className="text-[13px] text-cyan-300 hover:text-cyan-200 font-semibold">Edit</button>
                    {isAdmin && (
                      <button onClick={() => remove(c)} className="h-8 w-8 rounded-lg text-white/40 hover:text-red-300 hover:bg-red-400/10 inline-flex items-center justify-center">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center px-4 py-6 overflow-y-auto">
          <form onSubmit={submit} className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#12121a] p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="text-white font-semibold text-lg">
                {editing === 'new' ? 'Add customer' : `Edit · ${editing.name}`}
              </div>
              <button type="button" onClick={() => setEditing(null)} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Company name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <SelectField label="Region" value={form.region_id} onChange={(v) => setForm({ ...form, region_id: v })}
                options={[{ value: '', label: 'Select region' }, ...regions.map((r) => ({ value: r.id, label: r.name }))]} required />
              <TextField label="Contact name" value={form.contact_name || ''} onChange={(v) => setForm({ ...form, contact_name: v })} icon={Building2} />
              <TextField label="Contact email" type="email" value={form.contact_email || ''} onChange={(v) => setForm({ ...form, contact_email: v })} icon={Mail} />
              <TextField label="Contact phone" value={form.contact_phone || ''} onChange={(v) => setForm({ ...form, contact_phone: v })} icon={Phone} />
              <TextField label="Tax ID / GSTIN" value={form.tax_id || ''} onChange={(v) => setForm({ ...form, tax_id: v })} icon={FileText} placeholder="e.g. 27AAAAA0000A1Z5" />
              <TextField label="Country" value={form.country || ''} onChange={(v) => setForm({ ...form, country: v })} />
              <TextField label="State" value={form.state || ''} onChange={(v) => setForm({ ...form, state: v })} />
              <label className="block md:col-span-2">
                <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Billing address</span>
                <textarea rows={3} value={form.billing_address || ''} onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
                  className="mt-2 w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px] resize-y" />
              </label>
              <label className="block md:col-span-2">
                <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Notes</span>
                <textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-2 w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px] resize-y" />
              </label>
            </div>
            {err && <div className="mt-3 text-[13px] text-red-300">{err}</div>}
            <div className="mt-6 flex items-center justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="h-10 px-5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 font-semibold text-sm">Cancel</button>
              <button type="submit" disabled={saving} className="h-10 px-5 rounded-lg bg-emerald-400 hover:bg-emerald-300 disabled:opacity-60 text-black font-semibold text-sm inline-flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const TextField = ({ label, value, onChange, type = 'text', required, placeholder }) => (
  <label className="block">
    <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
      className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px]" />
  </label>
);

const SelectField = ({ label, value, onChange, options, required }) => (
  <label className="block">
    <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} required={required}
      className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px]">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

export default CustomersPage;
